import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ACCOUNT_SUPPLIER_DETAILS,
    atlas,
    BUYER_SUPPLIER_DETAILS,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW,
    SUPPLY_CHAIN_DIRECT_BUYER_OVERVIEW,
    SUPPLY_CHAIN_DIRECT_BUYER_SUPPLIERS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ListSellerSummaryRequest,
    ListSellerSummaryResponse
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    Color,
    getDynamicRouterLink,
    LifecycleAware,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    getRouterLinkById,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { PopoverPlacement } from 'ngx-smart-popover';
import { SupplyChainDirectBuyerAccountSharedService } from '../../supply-chain-direct-buyer-account-shared.service';

@LifecycleAware()
@Component({
    selector: 'early-payment-table',
    template: `
        <card
            [headerTitle]="
                !(payLater$ | async) ? supplierUtilizationI18n : supplierUtilizationTitleI18n
            "
            id="early-payment-table-card"
            class="card table-card"
            [hasFooter]="!(listSuppliersDataSource.loading$ | async)"
        >
            <div
                card-body
                class="card-body"
                [class.loading]="listSuppliersDataSource.loading$ | async"
            >
                <lsq-data-table
                    [dataSource]="listSuppliersDataSource"
                    [isPaginated]="false"
                    [appearance]="tableAppearance.Default"
                >
                    <lsq-column
                        [column]="{
                            header: supplierNameI18n,
                            key: 'name',
                            sortable: false,
                            width: '50%',
                            className: 'first-column'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <a
                                class="p not-bold supplier-name"
                                (click)="goSupplierDetails(row.sellerKey)"
                                queryParamsHandling="preserve"
                            >
                                <lsq-truncate [value]="row.name" [maxLength]="40"></lsq-truncate>
                            </a>
                        </ng-template>
                    </lsq-column>
                    <lsq-currency-column
                        [column]="{
                            header: openBalanceI18n,
                            key: 'openInvoiceAmount',
                            sortable: false,
                            width: '50%',
                            className: 'last-column'
                        }"
                    >
                    </lsq-currency-column>
                </lsq-data-table>
            </div>
            <div card-footer class="card-footer-table">
                <a
                    href="javascript:void(0)"
                    id="early-payment-table-card-view-all"
                    size="md"
                    (click)="navigateToSuppliers()"
                >
                    {{ allSuppliersI18n }}
                    ({{ listSuppliersDataSource.totalCount$ | async | number }})
                </a>
            </div>
        </card>
    `
})
export class BuyerEarlyPaymentTableComponent {
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly tableAppearance = TableAppearance;
    public isFinancingAccountPortalUser = false;

    public supplierUtilizationI18n = this._i18n({
        value: 'Early Payment Utilization',
        meaning: `Data describing how suppliers are using early payments`,
        description: 'Section title'
    });
    public supplierUtilizationTitleI18n = this._i18n({
        value: 'Top Suppliers by Open Balance',
        meaning: `Data describing how suppliers are using early payments`,
        description: 'Section title'
    });
    public supplierNameI18n = this._i18n({
        value: 'Supplier',
        description: 'Table column header'
    });
    public openBalanceI18n = this._i18n({
        value: 'Open Balance',
        description: 'Table column header'
    });
    public allSuppliersI18n = this._i18n({
        value: 'All Suppliers',
        meaning: `Click to view all your (the buyer's) suppliers in more detail`,
        description: 'Call-to-action button'
    });
    public readonly popoverPlacement: PopoverPlacement;
    public payLater$ = this._featureFlagService.isOn(FeatureFlags.PayLater);
    @Input() public listSuppliersDataSource!: LsqApiServiceListSource<
        ListSellerSummaryResponse,
        ListSellerSummaryRequest
    >;

    constructor(
        public responsiveService: ResponsiveService,
        private _router: Router,
        private _i18n: I18n,
        private _claimService: ClaimService,
        private _featureFlagService: FeatureFlagService,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService
    ) {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
    }

    public async navigateToSuppliers(): Promise<void> {
        await this._router.navigateByUrl(
            getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_BUYER_SUPPLIERS)
        );
    }

    public goSupplierDetails(sellerKey: string) {
        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        this.isFinancingAccountPortalUser
                            ? ACCOUNT_SUPPLIER_DETAILS
                            : BUYER_SUPPLIER_DETAILS,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { sellerKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: this
                            .isFinancingAccountPortalUser
                            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW
                            : SUPPLY_CHAIN_DIRECT_BUYER_OVERVIEW
                    }
                }
            )
        );
    }
}
